import type { LocationQuery, RouteLocationRaw } from 'vue-router';

import { isFeatureEnabled } from '@/config/featureFlags';
import { useSlaAccessStore } from '@/stores/sla-access';

/**
 * Route guard for the new wizard routes. Two gates, both fail closed to the
 * Organizations list so a stale URL never exposes the unfinished flow:
 * the slaOrgWizard feature flag, then the caller's SLA grants (GET sla/permissions,
 * docs/sla-access.md — no SLA role means the whole surface stays hidden).
 */
export async function orgWizardRouteGuard(): Promise<true | { name: 'organizations' }> {
    if (!isFeatureEnabled('slaOrgWizard')) return { name: 'organizations' };
    const access = useSlaAccessStore();
    await access.fetchOnce();
    return access.hasAnyAccess ? true : { name: 'organizations' };
}

/**
 * Target for the "Create new organization" button: the new wizard when the
 * flag is on, the legacy 5-step creation process otherwise.
 */
export function resolveCreateOrganizationRoute(): { name: 'org-wizard-create' | 'org-create' } {
    return { name: isFeatureEnabled('slaOrgWizard') ? 'org-wizard-create' : 'org-create' };
}

/**
 * The edit route of a draft, keeping the wizard's query (step, contract, group). The first save
 * of a new organization moves the URL here so a reload (or a shared link) resumes the same draft
 * instead of opening a fresh one.
 */
export function editDraftRoute(draftId: string, query: LocationQuery): RouteLocationRaw {
    return { name: 'org-wizard-edit', params: { draftId }, query };
}
